<template>
  <div>
    <!-- EXPANDED MODE: Single item with text (internal link) -->
    <v-list-item
      v-if="isSingleInternalItem && !rail"
      v-bind="singleItemProps"
      @click="handleMenuClick($event)"
      @mouseenter="hoveringSingleItem = true"
      @mouseleave="hoveringSingleItem = false"
      @focusin="hoveringSingleItem = true"
      @focusout="hoveringSingleItem = false"
    >
      <template #prepend>
        <v-icon :size="iconSize" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="[menuLinkText, 'custom-color']">
        {{ menuItem.text }}
      </v-list-item-title>
      <template
        v-if="!menuItem.noFavorite"
        #append
      >
        <FavoriteStar
          :is-favorite="isFavorite(menuItem.link)"
          :force-visible="hoveringSingleItem || isTouchDevice"
          @toggle="toggleFavorite(menuItem)"
        />
      </template>
    </v-list-item>

    <!-- EXPANDED MODE: Single item with text (external link) -->
    <v-list-item
      v-if="isSingleExternalItem && !rail"
      v-bind="singleExternalItemProps"
    >
      <template #prepend>
        <v-icon :size="iconSize" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="[menuLinkText, 'custom-color']">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item>

    <!-- RAIL MODE: Single item with tooltip (internal link) -->
    <v-tooltip
      v-if="isSingleInternalItem && rail"
      location="end"
      :text="menuItem.text"
    >
      <template #activator="{ props: tooltipProps }">
        <v-list-item
          v-bind="{ ...tooltipProps, ...singleItemProps }"
          :aria-label="menuItem.text"
          @click="handleMenuClick($event)"
          @mouseenter="hoveringSingleItem = true"
          @mouseleave="hoveringSingleItem = false"
          @focusin="hoveringSingleItem = true"
          @focusout="hoveringSingleItem = false"
        >
          <template #prepend>
            <div class="rail-icon-with-star">
              <FavoriteStar
                v-if="!menuItem.noFavorite"
                :is-favorite="isFavorite(menuItem.link)"
                :force-visible="hoveringSingleItem || isTouchDevice"
                size="x-small"
                class="rail-star-indicator"
                @toggle="toggleFavorite(menuItem)"
              />
              <v-icon :size="iconSize" :class="menuLinkText">
                {{ menuItem.icon || 'mdi-circle-medium' }}
              </v-icon>
            </div>
          </template>
        </v-list-item>
      </template>
    </v-tooltip>

    <!-- RAIL MODE: Single item with tooltip (external link) -->
    <v-tooltip
      v-if="isSingleExternalItem && rail"
      location="end"
      :text="menuItem.text"
    >
      <template #activator="{ props: tooltipProps }">
        <v-list-item
          v-bind="{ ...tooltipProps, ...singleExternalItemProps }"
          :aria-label="menuItem.text"
        >
          <template #prepend>
            <v-icon :size="iconSize" :class="menuLinkText">
              {{ menuItem.icon || 'mdi-circle-medium' }}
            </v-icon>
          </template>
        </v-list-item>
      </template>
    </v-tooltip>

    <!-- Legacy by moving to Vuetify 3 -->
    <!-- <v-list-item
      v-if="!menuItem.items && menuItem.fullLink && defaultFrontend.logServerUrl && menuItem.specKey === 'logsServer'"
      :id="`${menuItem.text}-${menuItem.key}`"
      :key="`${menuItem.text}-${menuItem.key}`"
      :active="menuItem.value"
      :href="defaultFrontend.logServerUrl"
      :exact="menuItem.exact"
      :disabled="menuItem.disabled"
      active-class="text-primary"
      :value="menuItem.value"
      link
      target="_blank"
      :data-cy="menuItem.text"
    >
      <template #prepend>
        <v-icon :small="small" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="menuLinkText">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item>

    <v-list-item
      v-if="!menuItem.items && menuItem.fullLink && menuItem.specKey === 'serviceDesk'"
      :id="`${menuItem.text}-${menuItem.key}`"
      :key="`${menuItem.text}-${menuItem.key}`"
      :active="menuItem.value"
      href="https://damudo.atlassian.net/servicedesk/"
      :exact="menuItem.exact"
      :disabled="menuItem.disabled"
      :value="menuItem.value"
      active-class="text-primary"
      link
      target="_blank"
    >
      <template #prepend>
        <v-icon :small="small" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="menuLinkText">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item>

    <v-list-item
      v-if="!menuItem.items && menuItem.fullLink && defaultFrontend.metricServerUrl && menuItem.specKey === 'metricServer'"
      :id="`${menuItem.text}-${menuItem.key}`"
      :key="`${menuItem.text}-${menuItem.key}`"
      :active="menuItem.value"
      :href="defaultFrontend.metricServerUrl"
      :exact="menuItem.exact"
      :disabled="menuItem.disabled"
      :value="menuItem.value"
      active-class="text-primary"
      color="primary"
      link
      target="_blank"
    >
      <template #prepend>
        <v-icon :small="small" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="menuLinkText">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item>

    <v-list-item
      v-if="!menuItem.items && menuItem.fullLink && defaultFrontend.visualisationServerUrl && menuItem.specKey === 'visualisationServer'"
      :id="`${menuItem.text}-${menuItem.key}`"
      :key="`${menuItem.text}-${menuItem.key}`"
      :active="menuItem.value"
      :href="defaultFrontend.visualisationServerUrl"
      :exact="menuItem.exact"
      :disabled="menuItem.disabled"
      :value="menuItemValue"
      active-class="text-primary"
      link
      target="_blank"
    >
      <template #prepend>
        <v-icon :small="small" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class=" {'text-white': $vuetify.theme.dark, 'text-grey ': menuItem.disabled }">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item> -->

    <!-- <v-list-item
      v-if="!menuItem.items && menuItem.fullLink && menuItem.specKey === 'swagger'"
      :id="`${menuItem.text}-${menuItem.key}`"
      :key="`${menuItem.text}-${menuItem.key}`"
      :active="menuItem.value"
      href="/api/swagger-ui/index.html"
      :exact="menuItem.exact"
      :value="menuItem.value"
      :disabled="menuItem.disabled"
      active-class="text-primary"
      link
      target="_blank"
    >
      <template #prepend>
        <v-icon :small="small" :class="menuLinkText">
          {{ menuItem.icon || 'mdi-circle-medium' }}
        </v-icon>
      </template>
      <v-list-item-title :class="{ 'custom-nav-color': !isThemeDark,'text-white': isThemeDark, 'text-grey': menuItem.disabled }">
        {{ menuItem.text }}
      </v-list-item-title>
    </v-list-item> -->

    <!-- EXPANDED MODE: Use existing v-list-group -->
    <v-list-group
      v-if="hasChildren && !rail"
      :key="`${menuItem.text}-${menuItem.key}`"
      :value="menuItem.key"
      link
    >
      <template #activator="{ props }">
        <v-list-item
          v-bind="props"
          :data-cy="menuItem.text"
          :aria-label="menuItem.text"
        >
          <template #prepend>
            <v-icon
              v-if="!subgroup"
              :size="iconSize"
              class="custom-nav-color"
            >
              {{ menuItem.icon || 'mdi-circle-medium' }}
            </v-icon>
          </template>
          <v-list-item-title class="latest-nav-custom-color">
            {{ menuItem.text }}
          </v-list-item-title>
        </v-list-item>
      </template>

      <v-list-item
        v-for="subItem in visibleSubItems"
        :key="`${subItem.text}-${subItem.key}`"
        v-bind="getSubItemProps(subItem)"
        active-class="customDrawerSelect"
        :value="subItem.key + '-expanded'"
        :data-cy="subItem.text"
        @click="!subItem.fullLink && handleMenuClick($event)"
        @mouseenter="hoveringSubItemKey = subItem.key"
        @mouseleave="hoveringSubItemKey = null"
        @focusin="hoveringSubItemKey = subItem.key"
        @focusout="hoveringSubItemKey = null"
      >
        <template #prepend>
          <v-icon size="small" :class="menuLinkText">
            {{ subItem.icon || 'mdi-circle-medium' }}
          </v-icon>
        </template>
        <v-list-item-title :class="[menuLinkText, 'custom-color']">
          {{ subItem.text }}
        </v-list-item-title>
        <template
          v-if="!subItem.fullLink"
          #append
        >
          <FavoriteStar
            :is-favorite="isFavorite(subItem.link)"
            :force-visible="hoveringSubItemKey === subItem.key || isTouchDevice"
            size="x-small"
            @toggle="toggleFavorite(subItem)"
          />
        </template>
      </v-list-item>
    </v-list-group>

    <!-- RAIL MODE: Use v-menu for flyout -->
    <v-menu
      v-if="hasChildren && rail"
      :key="`menu-${menuItem.key}`"
      v-model="flyoutOpen"
      location="end"
      :close-on-content-click="false"
      :open-on-hover="!isTouchDevice"
      :open-on-click="isTouchDevice"
      :open-delay="FLYOUT_OPEN_DELAY"
      :close-delay="FLYOUT_CLOSE_DELAY"
    >
      <template #activator="{ props }">
        <v-list-item
          v-bind="props"
          :data-cy="menuItem.text"
          :aria-label="menuItem.text"
          :class="{ 'rail-item-active': hasActiveChild }"
        >
          <template #prepend>
            <v-icon :size="iconSize" class="custom-nav-color">
              {{ menuItem.icon || 'mdi-circle-medium' }}
            </v-icon>
          </template>
        </v-list-item>
      </template>

      <!-- Flyout content -->
      <v-list
        density="compact"
        nav
        class="flyout-menu"
      >
        <!-- Header showing parent name -->
        <v-list-subheader class="flyout-header">
          {{ menuItem.text }}
        </v-list-subheader>

        <v-list-item
          v-for="subItem in visibleSubItems"
          :key="`flyout-${subItem.key}`"
          v-bind="getSubItemProps(subItem)"
          active-class="customDrawerSelect"
          :value="subItem.key + '-flyout'"
          :data-cy="subItem.text"
          @click="!subItem.fullLink && handleMenuClick($event)"
          @mouseenter="hoveringSubItemKey = subItem.key"
          @mouseleave="hoveringSubItemKey = null"
          @focusin="hoveringSubItemKey = subItem.key"
          @focusout="hoveringSubItemKey = null"
        >
          <template #prepend>
            <v-icon size="small" :class="menuLinkText">
              {{ subItem.icon || 'mdi-circle-medium' }}
            </v-icon>
          </template>
          <v-list-item-title :class="[menuLinkText, 'custom-color']">
            {{ subItem.text }}
          </v-list-item-title>
          <template
            v-if="!subItem.fullLink"
            #append
          >
            <FavoriteStar
              :is-favorite="isFavorite(subItem.link)"
              :force-visible="hoveringSubItemKey === subItem.key || isTouchDevice"
              size="x-small"
              @toggle="toggleFavorite(subItem)"
            />
          </template>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { bus } from '@/main'
import { mapGetters, mapState } from 'vuex'
import { useTheme } from 'vuetify'
import FavoriteStar from './FavoriteStar.vue'
import auth from '@/auth/index'
import {
  FLYOUT_OPEN_DELAY,
  FLYOUT_CLOSE_DELAY,
  FLYOUT_NAVIGATION_CLOSE_DELAY
} from '@/utils/constants'

/*
|---------------------------------------------------------------------
| Navigation Menu Item Component
|---------------------------------------------------------------------
|
| Navigation items for the FlowyNavMenu component
|
*/
export default {
  components: { FavoriteStar },
  props: {
    menuItem: {
      type: Object,
      default: () => {}
    },
    subgroup: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    rail: {
      type: Boolean,
      default: false
    },
    openFlyoutKey: {
      type: [String, null],
      default: null
    }
  },
  emits: ['update:flyout'],
  data() {
    return {
      isTouchDevice: false,
      closeTimeout: null,
      // Expose constants to template
      FLYOUT_OPEN_DELAY,
      FLYOUT_CLOSE_DELAY,
      // Hover/focus state for showing favorite stars
      hoveringSingleItem: false,
      hoveringSubItemKey: null
    }
  },
  computed: {
    ...mapState('app', ['defaultFrontend', 'userSettings', 'navigationSource']),
    ...mapGetters('app', ['isFavorite', 'isRTL']),

    // Check if current route is favorited (for suppressing main menu highlight)
    isCurrentRouteFavorited() {
      return this.isFavorite(this.$route.name)
    },

    // Should we suppress highlighting for favorited routes?
    // Symmetric logic with FavoritesSection.isActiveRoute():
    // - Favorites highlights when: navigationSource === 'favorites'
    // - Menu suppresses when: navigationSource === 'favorites'
    // Result: Favorites wins only for explicit favorites clicks,
    //         Menu wins for menu clicks AND direct URL navigation (default)
    shouldSuppressFavorite() {
      return this.isCurrentRouteFavorited && this.navigationSource === 'favorites'
    },

    // Flyout state - managed by parent via openFlyoutKey prop
    flyoutOpen: {
      get() {
        return this.menuItem?.key && this.openFlyoutKey === this.menuItem.key
      },
      set(isOpen) {
        if (this.menuItem?.key) {
          // Clear any pending timeout to prevent race conditions
          if (this.closeTimeout) {
            clearTimeout(this.closeTimeout)
            this.closeTimeout = null
          }
          this.$emit('update:flyout', this.menuItem.key, isOpen)
        }
      }
    },

    // Visibility conditions
    isSingleInternalItem() {
      return !this.menuItem.items && !this.menuItem.fullLink && !this.menuItem.hide
    },
    isSingleExternalItem() {
      return !this.menuItem.items && this.menuItem.fullLink && !this.menuItem.hide
    },
    hasChildren() {
      return this.menuItem.items && !this.menuItem.hide
    },

    // Icon size based on 'small' prop
    iconSize() {
      return this.small ? 'small' : 'default'
    },

    // Filtered visible sub-items (computed once, used in both modes)
    visibleSubItems() {
      return this.menuItem.items?.filter((item) => !item.hide) || []
    },

    // Unique value suffix based on rail mode (prevents duplicate ID warnings during mode transitions)
    valueSuffix() {
      return this.rail ? '-rail' : '-expanded'
    },

    // Shared props for single internal items (DRY)
    // The :to prop enables href attribute for right-click/middle-click support
    // The @click handler fires BEFORE navigation for navigationSource tracking
    singleItemProps() {
      // Explicitly compute active state: route matches AND not suppressed
      const routeMatches = this.$route.name === this.menuItem.link
      const isActive = routeMatches && !this.shouldSuppressFavorite

      return {
        key: `${this.menuItem.text}-${this.menuItem.key}`,
        to: { name: this.menuItem.link, params: { lang: this.$current_lang } },
        exact: this.menuItem.exact,
        disabled: this.menuItem.disabled,
        activeClass: 'customDrawerSelect',
        value: this.menuItem.key + this.valueSuffix,
        active: isActive,
        'data-cy': this.menuItem.text
      }
    },

    // Shared props for single external items (DRY)
    singleExternalItemProps() {
      return {
        key: `${this.menuItem.text}-${this.menuItem.key}`,
        active: undefined,  // External links always use undefined (no suppression needed)
        href: this.menuItem.link,
        target: '_blank',
        exact: this.menuItem.exact,
        disabled: this.menuItem.disabled,
        activeClass: 'customDrawerSelect',
        value: this.menuItem.key + this.valueSuffix,
        link: true,
        'data-cy': this.menuItem.text
      }
    },

    menuItemValue() {
      return this.menuItem.regex || this.menuItem.link
        ? this.$route.path.includes(this.menuItem.regex) || this.$route.path.includes(this.menuItem.link)
        : false
    },
    menuLinkText() {
      return { 'text-white': this.isThemeDark, 'text-grey': this.menuItem.disabled }
    },
    isThemeDark() {
      const theme = useTheme()
      return theme.global.name.value === 'dark'
    },
    hasActiveChild() {
      // Suppress main menu parent indicator when favorites should show the highlight
      if (this.shouldSuppressFavorite) return false

      if (!this.menuItem.items) return false
      const currentPath = this.$route.path
      const currentName = this.$route.name
      return this.menuItem.items.some((item) => {
        if (item.hide) return false
        if (item.link && currentName === item.link) return true
        if (item.regex && currentPath.includes(item.regex)) return true
        return false
      })
    }
  },
  watch: {
    '$route'() {
      // Close flyout on navigation
      if (this.flyoutOpen) {
        // Clear any existing timeout to prevent race conditions
        if (this.closeTimeout) {
          clearTimeout(this.closeTimeout)
        }
        // Brief delay for visual feedback before closing
        this.closeTimeout = setTimeout(() => {
          this.$emit('update:flyout', this.menuItem.key, false)
          this.closeTimeout = null
        }, FLYOUT_NAVIGATION_CLOSE_DELAY)
      }
    },
    // Force re-render when navigation source changes to ensure proper highlight state
    navigationSource() {
      this.$forceUpdate()
    }
  },
  mounted() {
    // Note: isTouchDevice is determined once at mount time and won't update if
    // device capabilities change (e.g., connecting/disconnecting a touchscreen).
    // This is acceptable since such changes are rare and would require a page
    // refresh for other UI elements to adapt anyway.
    try {
      this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    } catch {
      this.isTouchDevice = false // Conservative fallback
    }
    // Listen for favorite navigation events to ensure we update highlight state
    bus.$on('favoriteNavigation', this.onFavoriteNavigation)
  },
  beforeUnmount() {
    // Cleanup event listener
    bus.$off('favoriteNavigation', this.onFavoriteNavigation)
    // Cleanup pending timeout to prevent memory leaks
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout)
      this.closeTimeout = null
    }
  },
  methods: {
    // Force update when favorite navigation happens
    onFavoriteNavigation() {
      this.$forceUpdate()
    },
    refreshData(name) {
      bus.$emit('refreshData', name)
    },
    toggleFavorite(item) {
      this.$store.dispatch('app/toggleFavorite', item)
    },
    // Handle menu item click - set navigation source before :to navigates
    handleMenuClick(event) {
      // Skip side effects for modifier-key clicks (browser opens new tab via :to href)
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) return
      this.$store.commit('app/setNavigationSource', 'menu')
      bus.$emit('menuNavigation')
      // Navigation is handled by the :to prop - no router.push needed
    },
    // Generate props for sub-items (DRY - used in both expanded and rail modes)
    // The :to prop enables href attribute for right-click/middle-click support
    // The @click handler fires BEFORE navigation for navigationSource tracking
    // Swagger UI is only served to logged in users (DDEV-5848): a top level
    // navigation to the session endpoint sets the HttpOnly cookie and then
    // opens the UI. A plain link to the UI answers 401.
    swaggerSessionLink() {
      const token = auth.getToken()
      const tenant = auth.getTenantId()
      const params = new URLSearchParams()

      if (token) params.set('token', token)
      if (tenant) params.set('tenant', tenant)

      return `/api/swagger-ui/session?${params.toString()}`
    },
    getSubItemProps(subItem) {
      if (subItem.fullLink) {
        return {
          href: subItem.specKey === 'swagger' ? this.swaggerSessionLink() : subItem.link,
          target: '_blank',
          exact: subItem.exact,
          disabled: subItem.disabled
        }
      }

      // Explicitly compute active state: route matches AND not suppressed
      const routeMatches = this.$route.name === subItem.link
      const isActive = routeMatches && !this.shouldSuppressFavorite

      return {
        to: { name: subItem.link, params: { lang: this.$current_lang } },
        exact: subItem.exact,
        disabled: subItem.disabled,
        active: isActive
      }
    }
  }
}
</script>

<style lang="scss">
.v-list-item--dense .v-list-item__title,
.v-list-item--dense .v-list-item__subtitle,
.v-list--dense .v-list-item .v-list-item__title,
.v-list--dense .v-list-item .v-list-item__subtitle {
  line-height: 1.25rem;
}

.latest-nav-custom-color {
  color: rgb(var(--v-theme-customNavColor)) !important;
}

.v-list-item--active .custom-color{
  color: white !important;
}

// Badge-style star indicator for rail mode
.rail-icon-with-star {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .rail-star-indicator {
    position: absolute !important;
    top: 0;
    inset-inline-end: 0;
    transform: translate(50%, -50%);
    font-size: 10px;
    z-index: 1;
  }
}
</style>
